import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Masters, apiErrorMessage } from "../lib/api";
import { computeFinanceSummary, isV2Sale } from "../lib/finance";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Pill } from "../components/StatusPill";
import CompactDocumentGrid from "../components/CompactDocumentGrid";
import ReleaseCelebration from "../components/ReleaseCelebration";
import { TransitHoldBanner } from "../components/TransitHoldBadge";
import { ReleaseTransitButton } from "../components/ReleaseTransitButton";
import AssignVinButton from "../components/AssignVinButton";
import { BillingLedgerEditButton } from "../components/BillingLedgerBanner";
import SlaPauseButton from "../components/SlaPauseButton";
import ActivityTimeline from "../components/ActivityTimeline";
import PaymentReceiptsEditor from "../components/PaymentReceiptsEditor";
import EarlyDeliveryRequestButton from "../components/EarlyDeliveryRequestButton";
import DeliveryChecklistWizard from "../components/DeliveryChecklistWizard";
import { fmtDateTime, fmtDate } from "../lib/auditFormat";
import { toast } from "sonner";
import {
  ArrowLeft, FileCheck, Wrench, Banknote, Package, CheckCircle2, Truck, Pencil, Trash2,
  ShieldCheck, Receipt, Hash, Sparkles, Printer, FileBox, AlertOctagon,
  Phone, MessageCircle, Mail, Calendar, MapPin, User as UserIcon, IndianRupee,
  ClipboardList, Clock, Pause, Play, ChevronRight, FileText,
} from "lucide-react";
import { TASK_TYPE_LABELS, TASK_TIMELINE_ORDER } from "../lib/constants";
import { getRegistrationStage } from "../lib/registrationStatus";

const TASK_ICONS = {
  PDI_VIN: Wrench,
  PDI_READY: Truck,
  ACCOUNTS_INVOICE: Banknote,
  INSURANCE: ShieldCheck,
  ROAD_TAX: Receipt,
  HSRP: Hash,
  VAS_PP: Sparkles,
  VAS_AMC: Sparkles,
  VAS_RSA: Sparkles,
  SPARES: Package,
};
const TASK_LABELS = TASK_TYPE_LABELS;
const TASK_ORDER = TASK_TIMELINE_ORDER;

const fmt = (v) =>
  v == null || v === "" ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;

const applyDiscount = (amount, disc) => {
  const a = Number(amount) || 0;
  const v = Number(disc?.value) || 0;
  if (!v) return a;
  const cut = disc?.mode === "pct" ? (a * v) / 100 : v;
  return Math.max(0, a - cut);
};

// Workflow stages shown in the top stepper. Each stage groups one or more tasks.
const WORKFLOW_STAGES = [
  { id: "sales",     label: "Sales",             tasks: [] },           // submitted_at
  { id: "pdi",       label: "PDI",               tasks: ["PDI_VIN"] },
  { id: "accounts",  label: "Accounts",          tasks: ["ACCOUNTS_INVOICE"] },
  { id: "insurance", label: "Insurance",         tasks: ["INSURANCE"] },
  { id: "roadtax",   label: "Road Tax",          tasks: ["ROAD_TAX", "HSRP"] },
  { id: "vas",       label: "VAS",               tasks: ["VAS_PP", "VAS_AMC", "VAS_RSA"] },
  { id: "ready",     label: "Vehicle Readiness", tasks: ["PDI_READY", "SPARES"] },
  { id: "delivered", label: "Delivered",         tasks: [] },           // sale.status === delivered
];

export default function SalesDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations, users } = useApp();
  const [sale, setSale] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [mastersAll, setMastersAll] = useState({});
  const [released, setReleased] = useState(null);
  const [auditRev, setAuditRev] = useState(0);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([Sales.get(id), Tasks.list({ sale_id: id, with_sla: "true" })]);
    setSale(s); setTasks(t);
    setAuditRev((n) => n + 1);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const types = ["enquiry_source", "enquiry_sub_source", "finance_company", "regn_type", "vas_protection_plus", "vas_amc", "vas_rsa"];
      const out = {};
      await Promise.all(types.map(async (t) => { out[t] = await Masters.list({ type: t }); }));
      setMastersAll(out);
    })();
  }, []);

  const enquirySources = mastersAll.enquiry_source || [];
  const enquirySubSources = mastersAll.enquiry_sub_source || [];
  const financeCos = mastersAll.finance_company || [];
  const regnTypes = mastersAll.regn_type || [];
  const vasMap = (type, idVal) => {
    const m = (mastersAll[type] || []).find((x) => x.id === idVal);
    return m ? `${m.name}${m.price != null ? ` · ₹${m.price}` : ""}` : "—";
  };

  // ─── Payment math ───────────────────────────────────────────────────
  const paymentRows = useMemo(() => ([
    { key: "exshowroom", label: "Exshowroom" },
    { key: "registration", label: "Registration" },
    { key: "hypothecation", label: "Hypothecation" },
    { key: "insurance", label: "Insurance" },
    { key: "pp", label: "PP" },
    { key: "amc", label: "AMC" },
    { key: "rsa", label: "RSA" },
    { key: "accessories", label: "Accessories" },
    { key: "admin_incidental", label: "Admin & Incidental Charges" },
  ]), []);

  const paymentGross = useMemo(() => {
    if (!sale?.payment) return 0;
    return paymentRows.reduce((s, r) => s + (Number(sale.payment[r.key]) || 0), 0);
  }, [sale, paymentRows]);

  const totalDiscount = sale?.payment?.discounts?.total || null;
  const paymentNet = useMemo(
    () => applyDiscount(paymentGross, totalDiscount),
    [paymentGross, totalDiscount],
  );
  const discountAmount = paymentGross - paymentNet;

  // v2 pricing summary (Feb-2026 onwards). Falls through to the legacy path
  // below when this sale is a pre-v2 record.
  const summary = useMemo(() => computeFinanceSummary(sale || {}), [sale]);
  const isV2 = isV2Sale(sale);

  // Money received: from new payment_receipts collection if present, else legacy fields.
  const amountReceived = useMemo(() => {
    if (Array.isArray(sale?.payment_receipts) && sale.payment_receipts.length > 0) {
      return sale.payment_receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    }
    // Legacy fallback: down payment + dp accessories.
    return (Number(sale?.payment?.down_payment) || 0) +
           (Number(sale?.payment?.dp_accessories) || 0);
  }, [sale]);
  // For v2 sales the receivable ceiling is Net On-Road − DDA (not paymentNet).
  const receivableCeiling = isV2 ? summary.receivable : paymentNet;
  const amountRemaining = Math.max(0, receivableCeiling - amountReceived);
  const receivedPct = receivableCeiling > 0 ? Math.min(100, (amountReceived / receivableCeiling) * 100) : 0;

  const sortedTasks = useMemo(
    () => [...tasks].sort((a, b) => TASK_ORDER.indexOf(a.type) - TASK_ORDER.indexOf(b.type)),
    [tasks],
  );

  const customerAddress = useMemo(() => {
    const c = sale?.customer || {};
    return [c.address, c.at_po, c.city, c.district, c.state]
      .filter(Boolean).join(", ");
  }, [sale]);

  const accessoriesTotal = useMemo(() => {
    const items = sale?.accessory_items || [];
    return items.reduce((s, a) => s + (Number(a.price) || 0), 0);
  }, [sale]);

  // ─── Workflow stepper data ──────────────────────────────────────────
  const stageStates = useMemo(() => {
    const out = {};
    for (const stage of WORKFLOW_STAGES) {
      if (stage.id === "sales") {
        out.sales = sale?.submitted_at ? "completed" : "pending";
        continue;
      }
      if (stage.id === "delivered") {
        out.delivered = sale?.status === "delivered" ? "completed"
          : sale?.status === "ready_for_delivery" ? "in_progress" : "pending";
        continue;
      }
      const relevant = (tasks || []).filter((t) => stage.tasks.includes(t.type));
      if (relevant.length === 0) { out[stage.id] = "pending"; continue; }
      const allDone = relevant.every((t) => t.status === "completed" || t.status === "skipped");
      const anyDelayed = relevant.some(
        (t) => t.delay_marked || (t.sla?.status === "delayed" && t.status !== "completed"),
      );
      const anyPending = relevant.some((t) => t.status !== "completed" && t.status !== "skipped");
      out[stage.id] = allDone ? "completed"
        : anyDelayed ? "delayed"
        : anyPending ? "in_progress" : "pending";
    }
    return out;
  }, [sale, tasks]);

  const dseUser = useMemo(
    () => (users || []).find((u) => u.id === sale?.dse_id),
    [users, sale],
  );

  const [wizardOpen, setWizardOpen] = useState(false);

  if (!sale) return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>;

  const loc = locations.find((l) => l.id === sale.location_id);
  const isDraft = sale.status === "draft";

  const handleSubmit = async () => {
    try {
      const updated = await Sales.submit(sale.id);
      setReleased({ id: updated.id, sr_number: updated.sr_number });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Submit failed");
    }
  };

  const handleDeliver = () => {
    // Delivery is now a 5-step DSE-guided wizard — no immediate POST.
    setWizardOpen(true);
  };

  const handleWizardComplete = async (checklist) => {
    await Sales.deliver(sale.id, checklist);
    toast.success("Marked as delivered", { duration: 2400 });
    setWizardOpen(false);
    await load();
  };

  const handleDeleteDraft = async () => {
    try {
      await Sales.remove(sale.id);
      toast.success(isDraft ? "Draft deleted" : "Sale deleted", { duration: 2400 });
      navigate("/sales");
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  // Status pill on header — derives from stages, not raw status.
  const overallStatus = (() => {
    if (sale.status === "delivered") return { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" };
    if (sale.transit_hold?.active)   return { label: "On Hold",   cls: "bg-slate-100 text-slate-700 border-slate-200",     dot: "bg-slate-500" };
    if (Object.values(stageStates).includes("delayed"))
                                      return { label: "Delayed",  cls: "bg-rose-50 text-rose-700 border-rose-200",         dot: "bg-rose-500" };
    if (Object.values(stageStates).includes("in_progress"))
                                      return { label: "In Progress", cls: "bg-amber-50 text-amber-800 border-amber-200",   dot: "bg-amber-500" };
    if (isDraft)                      return { label: "Draft",    cls: "bg-slate-50 text-slate-600 border-slate-200",      dot: "bg-slate-400" };
    return { label: "Pending", cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" };
  })();

  // Promised delivery status badge
  const promisedBadge = (() => {
    const d = sale.delivery?.promised_date;
    if (!d) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const target = new Date(d); target.setHours(0,0,0,0);
    if (Number.isNaN(target.getTime())) return null;
    const diff = Math.round((target - today) / 86400000);
    if (sale.status === "delivered") return { label: "Delivered", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    if (diff < 0)  return { label: "Overdue",  cls: "bg-rose-50 text-rose-700 border-rose-200" };
    if (diff === 0) return { label: "Today",    cls: "bg-amber-50 text-amber-800 border-amber-200" };
    if (diff === 1) return { label: "Tomorrow", cls: "bg-amber-50 text-amber-800 border-amber-200" };
    return { label: "On Track", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  })();

  // Edit-Sale visibility:
  //   • Draft sales — DSE (owner) and Admin can edit. Status-quo behaviour.
  //   • Released sales — DSE, Sales Manager and Admin can edit. Locked-field
  //     enforcement happens inside `SalesForm` + backend `update_sale`.
  //   • Delivered sales — frozen for everyone EXCEPT Admin. Admin can edit
  //     a delivered record to correct post-handover data.
  const isAdmin = activeUser?.role === "Admin";
  const canEdit = (sale.status !== "delivered" || isAdmin) && (isDraft
    ? (activeUser?.role === "DSE" || isAdmin)
    : ["DSE", "Sales Manager", "Admin"].includes(activeUser?.role));

  // Early Delivery — derived state
  const ed = sale.early_delivery || {};
  const edStatus = ed.status || "none";  // none | pending | approved | rejected
  const pendingTaskTypes = (tasks || [])
    .filter((t) => t.status !== "completed" && t.status !== "skipped")
    .map((t) => t.type);
  const canRequestEarlyDelivery = (
    !isDraft &&
    sale.status !== "delivered" &&
    pendingTaskTypes.length > 0 &&
    ["DSE", "SalesManager", "Admin"].includes(activeUser?.role) &&
    edStatus !== "pending" &&
    edStatus !== "approved"
  );
  const earlyDeliveryApproved = edStatus === "approved" && sale.status !== "delivered";
  // Mark Delivered button visible when:
  //   • Normal flow: sale.status === "ready_for_delivery"
  //   • Conditional: early delivery has been approved (skip readiness gate)
  const canMarkDelivered = sale.status === "ready_for_delivery" || earlyDeliveryApproved;

  return (
    <div className="space-y-5" data-testid="sale-detail-page">
      {/* ─── Header ────────────────────────────────────────────── */}
      <div>
        <button
          onClick={() => navigate(-1)}
          className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Sales
        </button>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                {sale.customer?.name || "—"}
              </h1>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${overallStatus.cls}`}
                    data-testid="overall-status-pill">
                <span className={`h-1.5 w-1.5 rounded-full ${overallStatus.dot}`} />
                {overallStatus.label}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-slate-500">
              {sale.sr_number ? (
                <span className="font-mono-data font-semibold text-slate-700">{sale.sr_number}</span>
              ) : <span className="italic">Draft (no SR yet)</span>}
              {loc && <><span>·</span><span>{loc.name} ({loc.code})</span></>}
              {dseUser && <><span>·</span><span>Sales Executive: <strong className="text-slate-700">{dseUser.name}</strong></span></>}
            </div>
            {/* Early Delivery status banner-pill (visible whenever a request exists) */}
            {edStatus !== "none" && (
              <div className="mt-2">
                <EarlyDeliveryBadge
                  status={edStatus}
                  conditional={sale.status === "delivered" && ed.delivery_type === "conditional"}
                  reason={ed.current_request?.reason}
                  rejectionReason={ed.current_request?.rejection_reason}
                  decidedByName={ed.current_request?.decided_by_name}
                />
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button variant="outline"
                onClick={() => navigate(`/sales/${sale.id}/edit`)}
                className="gap-1.5"
                data-testid={isDraft ? "edit-draft-btn" : "edit-sale-btn"}>
                <Pencil className="h-4 w-4" /> {isDraft ? "Edit Draft" : "Edit Sale"}
              </Button>
            )}
            {/* Quick VIN assignment — visible to DSE/PDI/Admin on released sales
                that don't yet have a VIN. Calls the PDI release endpoint which
                completes PDI_VIN and spawns Accounts task. */}
            {!isDraft && !sale.vehicle?.vin &&
             (activeUser?.role === "DSE" || activeUser?.role === "PDI" || activeUser?.role === "Admin") && (
              <AssignVinButton
                saleId={sale.id}
                userId={activeUser?.id}
                onSaved={load}
                variant="default"
                size="default"
              />
            )}
            {isDraft && canEdit && (
              <DeleteDraftButton onConfirm={handleDeleteDraft} />
            )}
            {!isDraft && activeUser?.role === "Admin" && (
              <DeleteSaleButton sale={sale} onConfirm={handleDeleteDraft} />
            )}
            {isDraft && (
              <Button onClick={handleSubmit} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="submit-from-detail-btn">
                <FileCheck className="h-4 w-4" /> Release Sale
              </Button>
            )}
            {/* Early Delivery — Request button (Sales/DSE/Admin), when allowed */}
            {canRequestEarlyDelivery && (
              <EarlyDeliveryRequestButton
                sale={sale}
                pendingItems={pendingTaskTypes}
                onChanged={load}
              />
            )}
            {/* When ED is approved, show a single Early Delivery action group */}
            {earlyDeliveryApproved && (
              <Button
                onClick={handleDeliver}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                data-testid="early-delivery-mark-delivered-btn"
              >
                <CheckCircle2 className="h-4 w-4" /> Early Delivery · Submit Delivered
              </Button>
            )}
            {/* Standard ready-for-delivery path */}
            {sale.status === "ready_for_delivery" && !earlyDeliveryApproved && (
              <Button onClick={handleDeliver} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="deliver-btn">
                <Truck className="h-4 w-4" /> Mark Delivered
              </Button>
            )}
            {!isDraft && (
              <Button variant="outline"
                onClick={() => navigate(`/delivery-files/${sale.id}`)}
                className="gap-1.5" data-testid="open-delivery-file-btn">
                <FileBox className="h-4 w-4" /> Delivery File
              </Button>
            )}
            {!isDraft && (
              <Button variant="outline"
                onClick={() => navigate(`/sales/${sale.id}/print`)}
                className="gap-1.5" data-testid="print-sheet-btn">
                <Printer className="h-4 w-4" /> Customer Sheet
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Delivery Completion Checklist — full-screen 5-step wizard */}
      <DeliveryChecklistWizard
        open={wizardOpen}
        sale={{
          ...sale,
          _dse_name: (users.find((u) => u.id === sale.dse_id) || {}).name,
          _location_name: loc?.name,
        }}
        googleReviewLink={loc?.google_review_link}
        onClose={() => setWizardOpen(false)}
        onComplete={handleWizardComplete}
      />

      {/* Banners — preserve existing operational signals */}
      <TransitHoldBanner
        sale={sale}
        action={
          (activeUser?.role === "DSE" || activeUser?.role === "Accounts" || activeUser?.role === "Admin") ? (
            <ReleaseTransitButton saleId={sale.id} userId={activeUser?.id}
              vinPresent={!!sale.vehicle?.vin} onDone={load} />
          ) : null
        }
      />
      {!sale.transit_hold?.active && sale.transit_hold?.released_at && (
        <Card className="border-emerald-200 bg-emerald-50/60 p-4" data-testid="transit-released-log">
          <div className="flex items-start gap-3">
            <Truck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
            <div className="min-w-0 text-sm text-emerald-900">
              <div className="font-semibold">Transit Hold released · Workflow resumed</div>
              <div className="mt-0.5 text-xs text-emerald-800">
                {(() => {
                  const u = users.find((x) => x.id === sale.transit_hold.released_by);
                  const who = u ? `${u.name}${u.role ? ` (${u.role})` : ""}` : "—";
                  return <>Released by <strong>{who}</strong> on {fmtDateTime(sale.transit_hold.released_at)}</>;
                })()}
              </div>
              {sale.transit_hold.notes && (
                <div className="mt-1 text-xs italic text-emerald-700">“{sale.transit_hold.notes}”</div>
              )}
            </div>
          </div>
        </Card>
      )}
      <DelayBanner tasks={tasks} />

      {/* ─── Summary Strip ───────────────────────────────────────── */}
      <Card className="border-slate-200 shadow-od-subtle p-4">
        <div className="flex gap-4 overflow-x-auto md:grid md:grid-cols-3 lg:grid-cols-6 md:overflow-visible">
          <SummaryCell
            Icon={Receipt}
            label="Billing Ledger"
            value={sale.accounts?.billing_ledger_name || "—"}
            sub={
              <span className="inline-flex items-center gap-2">
                {sale.accounts?.billing_ledger_name ? (
                  <Link to={`/accounts/${sale.id}`} className="text-primary font-semibold hover:underline">
                    View ledger →
                  </Link>
                ) : (
                  <span className="italic text-slate-400">Awaiting Accounts</span>
                )}
                <BillingLedgerEditButton sale={sale} onUpdated={load} />
              </span>
            }
          />
          <SummaryCell
            Icon={Wrench}
            label="Vehicle"
            value={sale.vehicle?.model || "—"}
            sub={<span className="font-mono-data text-[11px]">{sale.vehicle?.vin ? `VIN ${sale.vehicle.vin}` : "VIN pending"}</span>}
          />
          <SummaryCell
            Icon={Calendar}
            label="Promised Delivery"
            value={fmtDate(sale.delivery?.promised_date) || "—"}
            sub={promisedBadge ? (
              <span className={`inline-flex w-fit items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${promisedBadge.cls}`}>
                {promisedBadge.label}
              </span>
            ) : <span className="italic text-slate-400">Not set</span>}
          />
          <SummaryCell
            Icon={IndianRupee}
            label="Total Sale Price"
            value={fmt(paymentNet)}
            sub={paymentGross !== paymentNet ? (
              <span className="text-[11px] text-slate-500">
                Gross {fmt(paymentGross)} · Disc {fmt(discountAmount)}
              </span>
            ) : null}
          />
          <SummaryCell
            Icon={CheckCircle2}
            label="Amount Received"
            value={fmt(amountReceived)}
            tone="emerald"
            sub={paymentNet > 0 ? <span className="text-[11px] text-emerald-700 font-semibold">{receivedPct.toFixed(1)}%</span> : null}
          />
          <SummaryCell
            Icon={AlertOctagon}
            label="Amount Remaining"
            value={fmt(amountRemaining)}
            tone={amountRemaining > 0 ? "amber" : "emerald"}
          />
        </div>
      </Card>

      {/* ─── Body: Left 70% / Right 30% ────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* ─────── LEFT COLUMN ─────── */}
        <div className="space-y-5 min-w-0">
          {/* Task Progress */}
          <Card className="border-slate-200 shadow-od-subtle" data-testid="task-progress-top">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-display text-lg font-bold">Task Progress</CardTitle>
              {sale.submitted_at && (
                <span className="text-[11px] font-medium text-slate-500">
                  Released {fmtDateTime(sale.submitted_at)}
                </span>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              <WorkflowStepper stages={WORKFLOW_STAGES} stageStates={stageStates} />
              {isDraft ? (
                <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">
                  Tasks will be auto-created on Release.
                </div>
              ) : sortedTasks.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-4">No tasks</div>
              ) : (
                <TaskCardList
                  tasks={sortedTasks}
                  sale={sale}
                  activeUser={activeUser}
                  onReload={load}
                />
              )}
            </CardContent>
          </Card>

          {/* Payment Receipts — Sales Manager is view-only (spec addendum,
              Feb-2026). PDI / Spares are always locked out. */}
          {!isDraft && (
            <PaymentReceiptsEditor
              saleId={sale.id}
              userId={activeUser?.id}
              disabled={["PDI", "Spares", "SalesManager"].includes(activeUser?.role)}
              testid="detail-receipts"
            />
          )}

          {/* Collapsible details */}
          <Accordion
            type="multiple"
            defaultValue={["documents"]}
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            data-testid="sale-detail-accordion"
          >
            <Section value="finance" title="Finance & Insurance"
              summary={describeFinance(sale, financeCos)}
              badge={sale.finance?.buy_mode ? "Complete" : "Pending"}
              badgeTone={sale.finance?.buy_mode ? "emerald" : "slate"}
            >
              <KVGrid>
                <KV label="Buy Mode" value={sale.finance?.buy_mode === "finance" ? "Finance" : sale.finance?.buy_mode === "cash" ? "Cash" : "—"} />
                <KV label="Finance Company"
                    value={financeCos.find((m) => m.id === sale.finance?.finance_company_id)?.name} />
                {sale.finance?.buy_mode === "finance" && (
                  <>
                    <KV label="Loan Amount" value={fmt(sale.finance?.loan_amount)} />
                    <KV label="Hypothecation"
                        value={sale.finance?.hypothecation === true ? "Yes" : sale.finance?.hypothecation === false ? "No" : "—"} />
                  </>
                )}
                <KV label="Insurance"
                    value={sale.insurance?.type === "purchased" ? "Purchased"
                          : sale.insurance?.type === "customer_self" ? "Customer Self" : "—"} />
                {sale.insurance?.type === "purchased" && (
                  <KV label="Plan Type"
                      value={sale.insurance?.plan_type === "1+5" ? "1 OD + 5 TP"
                            : sale.insurance?.plan_type === "5+5" ? "5 OD + 5 TP" : "—"} />
                )}
                <KV label="Nominee" value={sale.insurance?.nominee_name} />
                <KV label="Relation" value={sale.insurance?.relation} />
                <KV label="Nominee Age" value={sale.insurance?.nominee_age} />
              </KVGrid>
            </Section>

            <Section value="registration" title="Registration"
              summary={(() => {
                const { stage, regNo } = getRegistrationStage(sale);
                return regNo ? `${stage.sub} · ${regNo}` : stage.sub;
              })()}
              badge={getRegistrationStage(sale).stage.label}
              badgeTone={getRegistrationStage(sale).stage.tone}
            >
              <div className="space-y-4">
                {/* Simplified 3-stage banner */}
                {(() => {
                  const { stage, regNo } = getRegistrationStage(sale);
                  return (
                    <div className={`flex items-start gap-3 rounded-md border p-3 ${stage.cls}`}
                         data-testid={`reg-stage-banner-${stage.key}`}>
                      <span className={`mt-0.5 inline-flex h-2 w-2 flex-shrink-0 rounded-full ${stage.dot}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-bold">{stage.label}</div>
                        <div className="mt-0.5 text-xs opacity-80">
                          {regNo ? `Registration No · ${regNo}` : stage.sub}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <KVGrid>
                  <KV label="Regn Type" value={(() => {
                    const m = regnTypes.find((x) => x.id === sale.registration?.regn_type_id);
                    return m ? (m.code ? `${m.code} — ${m.name}` : m.name) : "—";
                  })()} />
                  <KV label="RTO Office" value={sale.registration?.rto_office} />
                  <KV label="Choice No" value={sale.registration?.choice_no} />
                  <KV label="Registration No." value={sale.registration?.registration_no} />
                </KVGrid>
              </div>
            </Section>

            <Section value="vas" title="VAS Services"
              summary={describeVAS(sale)}
              badge={stageStates.vas === "completed" ? "Complete" : stageStates.vas === "in_progress" ? "In Progress" : "Pending"}
              badgeTone={stageStates.vas === "completed" ? "emerald" : stageStates.vas === "in_progress" ? "amber" : "slate"}
            >
              <KVGrid>
                <KV label="Protection Plus" value={
                  sale.vas?.pp_opted === false ? <span className="italic text-slate-500">Customer Not Opted</span>
                  : vasMap("vas_protection_plus", sale.vas?.pp_id)
                } />
                <KV label="AMC" value={
                  sale.vas?.amc_opted === false ? <span className="italic text-slate-500">Customer Not Opted</span>
                  : vasMap("vas_amc", sale.vas?.amc_id)
                } />
                <KV label="RSA" value={
                  sale.vas?.rsa_opted === false ? <span className="italic text-slate-500">Customer Not Opted</span>
                  : vasMap("vas_rsa", sale.vas?.rsa_id)
                } />
              </KVGrid>
            </Section>

            <Section value="accessories" title={`Accessories${(sale.accessory_items?.length || 0) ? ` (${sale.accessory_items.length})` : ""}`}
              summary={`${sale.accessory_items?.length || 0} item${(sale.accessory_items?.length || 0) === 1 ? "" : "s"} · ${fmt(accessoriesTotal)}`}
              badge={(sale.accessory_items?.length || 0) > 0 ? "Listed" : "None"}
              badgeTone={(sale.accessory_items?.length || 0) > 0 ? "emerald" : "slate"}
            >
              {(sale.accessory_items?.length || 0) === 0 ? (
                <div className="text-sm text-slate-500 py-2">No accessory items.</div>
              ) : (
                <div className="overflow-hidden rounded-md border border-slate-200">
                  <table className="min-w-full divide-y divide-border text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      <tr><th className="px-3 py-2">Item</th><th className="px-3 py-2 w-40 text-right">Price (₹)</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-white">
                      {sale.accessory_items.map((a, i) => (
                        <tr key={`${a.name || "item"}-${i}`}>
                          <td className="px-3 py-2 text-slate-900">{a.name}</td>
                          <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                            ₹{Number(a.price || 0).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td className="px-3 py-2.5 text-sm font-semibold text-slate-900">Total</td>
                        <td className="px-3 py-2.5 text-right font-mono-data text-base font-semibold text-primary">
                          ₹{accessoriesTotal.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section value="payment" title="Payment Breakup"
              summary={`Net ${fmt(paymentNet)}${discountAmount > 0 ? ` · Discount ${fmt(discountAmount)}` : ""}`}
              badge={paymentNet > 0 ? "Configured" : "Pending"}
              badgeTone={paymentNet > 0 ? "emerald" : "slate"}
            >
              <div className="overflow-hidden rounded-md border border-slate-200">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Component</th>
                      <th className="px-3 py-2 w-40 text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-white">
                    {paymentRows.map((r) => {
                      const val = sale.payment?.[r.key];
                      const isZero = !val || Number(val) === 0;
                      // In v2 layout, accessories are shown *after* the Gross line, so
                      // skip the inline row here for v2 sales.
                      if (isV2 && r.key === "accessories") return null;
                      // Hide rows with no value to keep the breakup compact, except the
                      // mandatory exshowroom + registration which we always show.
                      if (isZero && !["exshowroom", "registration"].includes(r.key)) return null;
                      return (
                        <tr key={r.key}>
                          <td className="px-3 py-2 text-slate-900">{r.label}</td>
                          <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                            {fmt(val)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50">
                      <td className="px-3 py-2 text-sm font-semibold text-slate-700">
                        {isV2 ? "Gross Amount" : "Gross"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-data text-sm font-semibold text-slate-700">
                        ₹{(isV2 ? summary.gross : paymentGross).toLocaleString("en-IN")}
                      </td>
                    </tr>
                    {!isV2 && (
                      <tr>
                        <td className="px-3 py-2 text-sm text-rose-700">
                          Discount
                          {totalDiscount?.mode === "pct" && totalDiscount?.value
                            ? ` (${totalDiscount.value}%)` : ""}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-data text-rose-700">
                          − ₹{discountAmount.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {isV2 && (
                      <tr>
                        <td className="px-3 py-2 text-slate-700">Accessories
                          <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                            auto · from Accessories
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                          ₹{summary.accessories.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-primary/5">
                      <td className="px-3 py-2.5 text-sm font-bold text-primary">Net On-Road Price</td>
                      <td className="px-3 py-2.5 text-right font-mono-data text-base font-bold text-primary">
                        ₹{(isV2 ? summary.netOnRoad : paymentNet).toLocaleString("en-IN")}
                      </td>
                    </tr>
                    {/* v2: Discount reduces receivable (spec addendum Feb-2026). Placed
                        between NRP and DDA. Hidden when no discount configured. */}
                    {isV2 && summary.discount > 0 && (
                      <tr>
                        <td className="px-3 py-2 text-sm text-rose-700">
                          Discount
                          {summary.discountConfig?.mode === "pct" && summary.discountConfig?.value
                            ? ` (${summary.discountConfig.value}%)` : ""}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-data text-rose-700">
                          − ₹{summary.discount.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                    {isV2 ? (
                      sale.finance?.buy_mode === "finance" && (
                        <>
                          <tr>
                            <td className="px-3 py-2 text-sm text-slate-700">Dealer Disbursement Amount (DDA)</td>
                            <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                              {summary.dda > 0 ? `− ₹${summary.dda.toLocaleString("en-IN")}` : "—"}
                            </td>
                          </tr>
                          <tr className="bg-emerald-50/50">
                            <td className="px-3 py-2 text-sm font-semibold text-emerald-800">To Receive from Customer</td>
                            <td className="px-3 py-2 text-right font-mono-data font-bold text-emerald-800">
                              ₹{summary.receivable.toLocaleString("en-IN")}
                            </td>
                          </tr>
                        </>
                      )
                    ) : (
                      sale.finance?.buy_mode === "finance" && (
                        <>
                          <tr>
                            <td className="px-3 py-2 text-sm text-slate-700">Down Payment</td>
                            <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                              {fmt(sale.payment?.down_payment)}
                            </td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 text-sm text-slate-700">DP Accessories</td>
                            <td className="px-3 py-2 text-right font-mono-data text-slate-900">
                              {fmt(sale.payment?.dp_accessories)}
                            </td>
                          </tr>
                          <tr className="bg-emerald-50/50">
                            <td className="px-3 py-2 text-sm font-semibold text-emerald-800">To Receive from Customer</td>
                            <td className="px-3 py-2 text-right font-mono-data font-bold text-emerald-800">
                              {fmt((Number(sale.payment?.down_payment) || 0) + (Number(sale.payment?.dp_accessories) || 0))}
                            </td>
                          </tr>
                        </>
                      )
                    )}
                    {isV2 && sale.finance?.buy_mode !== "finance" && (
                      <tr className="bg-emerald-50/50">
                        <td className="px-3 py-2 text-sm font-semibold text-emerald-800">To Receive from Customer</td>
                        <td className="px-3 py-2 text-right font-mono-data font-bold text-emerald-800">
                          ₹{summary.receivable.toLocaleString("en-IN")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section value="delivery" title="Delivery"
              summary={sale.delivery?.promised_date
                ? `${fmtDate(sale.delivery.promised_date)}${sale.delivery?.promised_time ? ` · ${sale.delivery.promised_time}` : ""}`
                : "Not scheduled"}
              badge={stageStates.delivered === "completed" ? "Delivered"
                : stageStates.delivered === "in_progress" ? "Ready"
                : sale.delivery?.promised_date ? "Scheduled" : "Pending"}
              badgeTone={stageStates.delivered === "completed" ? "emerald"
                : stageStates.delivered === "in_progress" ? "emerald"
                : sale.delivery?.promised_date ? "amber" : "slate"}
            >
              <KVGrid>
                <KV label="Promised Date" value={fmtDate(sale.delivery?.promised_date)} />
                <KV label="Promised Time" value={sale.delivery?.promised_time} />
                <KV label="PDI Readiness"
                    value={(tasks || []).find((t) => t.type === "PDI_READY")?.status === "completed"
                      ? "Ready" : "Pending"} />
                <KV label="Notes" value={sale.delivery?.notes} full />
              </KVGrid>
            </Section>

            <Section value="documents" title="Documents"
              summary={(() => {
                const count = (tasks || []).length; // crude — will be overridden by accordion content
                return "All sale documents in one grid";
              })()}
              badge="View"
              badgeTone="primary"
              fullWidth
            >
              <CompactDocumentGrid
                saleId={sale.id}
                uploadedBy={activeUser?.id}
                readOnly={
                  activeUser?.role !== "Admin" &&
                  activeUser?.role !== "DSE" &&
                  activeUser?.role !== "PDI"
                }
              />
            </Section>

            <Section value="enquiry" title="Enquiry"
              summary={sale.enquiry?.enquiry_number ? `Booking #${sale.enquiry.enquiry_number}` : "No enquiry recorded"}
              badge={sale.enquiry?.enquiry_number ? "Logged" : "—"}
              badgeTone={sale.enquiry?.enquiry_number ? "emerald" : "slate"}
            >
              <KVGrid>
                <KV label="Enquiry Number" value={sale.enquiry?.enquiry_number} />
                <KV label="Booking Date" value={fmtDate(sale.enquiry?.booking_date)} />
                <KV label="Source"
                    value={enquirySources.find((m) => m.id === sale.enquiry?.enquiry_source_id)?.name} />
                <KV label="Sub Source"
                    value={enquirySubSources.find((m) => m.id === sale.enquiry?.enquiry_sub_source_id)?.name} />
              </KVGrid>
            </Section>
          </Accordion>
        </div>

        {/* ─────── RIGHT COLUMN ─────── */}
        <div className="space-y-5 min-w-0">
          <ActivityTimeline saleId={sale.id} refreshKey={auditRev} />

          <RightCard title="Customer Details" viewAllLabel="View All"
                     onViewAll={() => navigate(`/sales/${sale.id}/edit`)}
                     testid="customer-details-card">
            <IconRow Icon={Phone} value={sale.customer?.phone} href={sale.customer?.phone ? `tel:${sale.customer.phone}` : null} />
            {sale.customer?.alt_phone && (
              <IconRow Icon={MessageCircle} value={sale.customer.alt_phone}
                       href={`https://wa.me/91${sale.customer.alt_phone}`} hint="WhatsApp" />
            )}
            <IconRow Icon={Mail} value={sale.customer?.email}
                     href={sale.customer?.email ? `mailto:${sale.customer.email}` : null} />
            <IconRow Icon={Calendar} value={fmtDate(sale.customer?.dob)} hint="DOB" />
            <IconRow Icon={ShieldCheck} value={sale.customer?.aadhaar} hint="Aadhaar" />
            <IconRow Icon={Hash} value={sale.customer?.gst_no} hint="GST" />
            {customerAddress && (
              <IconRow Icon={MapPin} value={customerAddress} hint="Address" multiline />
            )}
          </RightCard>

          <RightCard title="Vehicle Details" viewAllLabel="View All"
                     onViewAll={() => navigate(`/sales/${sale.id}/edit`)}
                     testid="vehicle-details-card">
            <KVRow label="Model" value={sale.vehicle?.model} />
            <KVRow label="VIN" value={sale.vehicle?.vin || <Pill tone="warning">Pending PDI</Pill>} mono />
            <KVRow label="Engine No." value={sale.vehicle?.engine_no} mono />
            <KVRow label="Color" value={sale.vehicle?.color_code || sale.vehicle?.color} />
            <KVRow label="Registration No." value={sale.registration?.registration_no} mono />
            {!isDraft && !sale.vehicle?.vin &&
             (activeUser?.role === "DSE" || activeUser?.role === "PDI" || activeUser?.role === "Admin") && (
              <div className="pt-2">
                <AssignVinButton
                  saleId={sale.id}
                  userId={activeUser?.id}
                  onSaved={load}
                  testId="assign-vin-vehicle-card-btn"
                />
              </div>
            )}
          </RightCard>
        </div>
      </div>

      <ReleaseCelebration
        open={!!released}
        srNumber={released?.sr_number}
        onClose={() => setReleased(null)}
        onViewSale={() => setReleased(null)}
      />
    </div>
  );
}

// ─── Layout helpers ──────────────────────────────────────────────

function SummaryCell({ Icon, label, value, sub, tone }) {
  const toneCls = tone === "emerald" ? "text-emerald-700"
                : tone === "amber"   ? "text-amber-700"
                : "text-slate-900";
  return (
    <div className="flex min-w-[180px] items-start gap-3 shrink-0 md:shrink md:min-w-0">
      {Icon && (
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-slate-50 border border-slate-200 shrink-0">
          <Icon className="h-4 w-4 text-slate-600" />
        </span>
      )}
      <div className="min-w-0 flex flex-col gap-0.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </span>
        <span className={`text-sm font-bold truncate ${toneCls}`}>{value}</span>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

function WorkflowStepper({ stages, stageStates }) {
  return (
    <ol className="flex w-full items-start gap-1 overflow-x-auto pb-1" data-testid="workflow-stepper">
      {stages.map((s, idx) => {
        const state = stageStates[s.id] || "pending";
        const dotCls =
          state === "completed" ? "bg-emerald-500 text-white border-emerald-500"
          : state === "in_progress" ? "bg-amber-500 text-white border-amber-500"
          : state === "delayed" ? "bg-rose-500 text-white border-rose-500"
          : "bg-white text-slate-400 border-slate-300";
        const lineCls = idx < stages.length - 1
          ? (stageStates[stages[idx + 1].id] === "completed" || state === "completed"
              ? "bg-emerald-400" : "bg-slate-200")
          : "bg-transparent";
        const labelCls =
          state === "completed" ? "text-emerald-700"
          : state === "in_progress" ? "text-amber-700"
          : state === "delayed" ? "text-rose-700"
          : "text-slate-500";
        const statusText = state === "completed" ? "Completed"
          : state === "in_progress" ? "In Progress"
          : state === "delayed" ? "Delayed" : "Pending";
        return (
          <li key={s.id} className="flex flex-col items-center min-w-[78px] flex-1"
              data-testid={`stage-${s.id}-${state}`}>
            <div className="flex items-center w-full">
              <span className={`h-[2px] flex-1 ${idx === 0 ? "bg-transparent" : lineCls}`} />
              <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-[11px] font-bold ${dotCls}`}>
                {state === "completed" ? <CheckCircle2 className="h-4 w-4" />
                  : state === "in_progress" ? <Clock className="h-3.5 w-3.5" />
                  : state === "delayed" ? <AlertOctagon className="h-3.5 w-3.5" />
                  : (idx + 1)}
              </span>
              <span className={`h-[2px] flex-1 ${idx === stages.length - 1 ? "bg-transparent" : lineCls}`} />
            </div>
            <span className="mt-2 text-[11px] font-bold text-slate-700 text-center leading-tight">{s.label}</span>
            <span className={`mt-0.5 text-[10px] font-medium ${labelCls}`}>{statusText}</span>
          </li>
        );
      })}
    </ol>
  );
}

function TaskCardList({ tasks, sale, activeUser, onReload }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? tasks : tasks.slice(0, 4);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {visible.map((t) => (
          <TaskCard key={t.id} task={t} sale={sale} activeUser={activeUser} onReload={onReload} />
        ))}
      </div>
      {tasks.length > 4 && (
        <div className="text-center">
          <button type="button" onClick={() => setShowAll((v) => !v)}
                  className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline"
                  data-testid="view-all-tasks-btn">
            {showAll ? "Show less" : `View All Tasks (${tasks.length})`}
            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAll ? "rotate-90" : ""}`} />
          </button>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task: t, sale, activeUser, onReload }) {
  const Icon = TASK_ICONS[t.type] || CheckCircle2;
  const done = t.status === "completed";
  const skipped = t.status === "skipped";
  const isPdi = t.type === "PDI_VIN" || t.type === "PDI_READY";
  const isAccounts = t.type === "ACCOUNTS_INVOICE";
  const boPathMap = {
    INSURANCE: "insurance", ROAD_TAX: "road-tax", HSRP: "hsrp",
    VAS_PP: "vas-pp", VAS_AMC: "vas-amc", VAS_RSA: "vas-rsa",
  };
  const isBackOffice = !!boPathMap[t.type];
  const isSpares = t.type === "SPARES";
  const canComplete =
    !done && !skipped &&
    (activeUser?.role === t.assigned_role || activeUser?.role === "Admin") &&
    !isPdi && !isAccounts && !isBackOffice && !isSpares &&
    !(t.type === "PDI_VIN" && !sale.vehicle?.vin);

  const sla = t.sla;
  const isDelayed = sla?.status === "delayed" && !done && !skipped;
  const isInProgress = !done && !skipped && !isDelayed;

  const cardCls = done || skipped
    ? "border-emerald-200 bg-emerald-50/40"
    : isDelayed
      ? "border-rose-200 bg-rose-50/40"
      : "border-amber-200 bg-amber-50/40";
  const iconCls = done || skipped
    ? "border-emerald-300 bg-white text-emerald-600"
    : isDelayed
      ? "border-rose-300 bg-white text-rose-600"
      : "border-amber-300 bg-white text-amber-600";

  const statusLabel = done ? "Completed" : skipped ? "Skipped" : isDelayed ? "Delayed" : "In Progress";
  const statusBadgeCls = done ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : skipped ? "bg-slate-100 text-slate-600 border-slate-200"
    : isDelayed ? "bg-rose-100 text-rose-700 border-rose-200"
    : "bg-amber-100 text-amber-700 border-amber-200";

  const remainText = (() => {
    if (sla?.remaining_minutes == null) return null;
    const m = sla.remaining_minutes;
    const abs = Math.abs(m);
    const txt = abs >= 60 ? `${Math.floor(abs / 60)}h ${Math.round(abs % 60)}m` : `${Math.round(abs)}m`;
    return m < 0 ? `${txt} overdue` : `${txt} left`;
  })();

  return (
    <div className={`rounded-lg border p-3.5 shadow-od-subtle space-y-2.5 ${cardCls}`}
         data-testid={`task-${t.type}`}>
      <div className="flex items-start justify-between gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${iconCls}`}>
          {done || skipped ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusBadgeCls}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {statusLabel}
        </span>
      </div>
      <div className="font-display text-sm font-bold text-slate-900">
        {TASK_LABELS[t.type] || t.type}
      </div>
      {sla?.budget_minutes != null && (
        <div className="flex items-center justify-between text-[11px] text-slate-600">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> SLA {Math.round(sla.budget_minutes)}m
          </span>
          {remainText && (
            <span className={done ? "text-emerald-700" : isDelayed ? "text-rose-700 font-semibold" : "text-amber-700 font-semibold"}>
              {remainText}
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-[10px]">
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-400">Created</div>
          <div className="text-slate-700">{t.created_at ? fmtDateTime(t.created_at) : "—"}</div>
        </div>
        <div>
          <div className="font-bold uppercase tracking-wider text-slate-400">
            {done || skipped ? "Completed" : "Due"}
          </div>
          <div className="text-slate-700">
            {done || skipped
              ? (t.completed_at ? fmtDateTime(t.completed_at) : "—")
              : (sla?.due_at ? fmtDateTime(sla.due_at) : "—")}
          </div>
        </div>
      </div>
      {t.notes && <p className="text-[11px] italic text-slate-600 line-clamp-2">{t.notes}</p>}
      {!done && !skipped && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          {isPdi && (
            <Link to={`/pdi/${sale.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-primary/90"
                  data-testid={`open-pdi-${t.type}`}>
              <FileCheck className="h-3 w-3" /> Open in PDI
            </Link>
          )}
          {isAccounts && (activeUser?.role === "Accounts" || activeUser?.role === "Admin") && (
            <Link to={`/accounts/${sale.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-primary/90"
                  data-testid="open-accounts-workspace-btn">
              <FileCheck className="h-3 w-3" /> Verify & Invoice
            </Link>
          )}
          {isBackOffice && (activeUser?.role === "BackOffice" || activeUser?.role === "Admin") && (
            <Link to={`/back-office/${boPathMap[t.type]}/${sale.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-primary/90"
                  data-testid={`open-bo-${t.type}-btn`}>
              <FileCheck className="h-3 w-3" /> Open in Back Office
            </Link>
          )}
          {isSpares && (activeUser?.role === "Spares" || activeUser?.role === "Admin") && (
            <Link to={`/spares/${sale.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-primary/90"
                  data-testid="open-spares-workspace-btn">
              <FileCheck className="h-3 w-3" /> Fulfil Accessories
            </Link>
          )}
          {canComplete && <CompleteTaskBtn taskId={t.id} type={t.type} onDone={onReload} />}
          <SlaPauseButton task={t} onChange={onReload} />
        </div>
      )}
    </div>
  );
}

function RightCard({ title, viewAllLabel, onViewAll, children, testid }) {
  return (
    <Card className="border-slate-200 shadow-od-subtle" data-testid={testid}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="font-display text-base font-bold">{title}</CardTitle>
        {viewAllLabel && onViewAll && (
          <button type="button" onClick={onViewAll}
                  className="text-xs font-bold text-primary hover:underline">
            {viewAllLabel}
          </button>
        )}
      </CardHeader>
      <CardContent className="space-y-2.5">{children}</CardContent>
    </Card>
  );
}

function IconRow({ Icon, value, href, hint, multiline }) {
  if (!value) return null;
  const text = (
    <span className={`text-sm text-slate-900 ${multiline ? "" : "truncate"}`}>{value}</span>
  );
  return (
    <div className="flex items-start gap-2.5">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-50 border border-slate-200 shrink-0">
        <Icon className="h-3.5 w-3.5 text-slate-600" />
      </span>
      <div className="min-w-0 flex flex-col gap-0.5">
        {href ? (
          <a href={href} target={href.startsWith("http") ? "_blank" : undefined}
             rel="noreferrer"
             className="text-sm text-primary hover:underline truncate font-medium">
            {value}
          </a>
        ) : text}
        {hint && <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{hint}</span>}
      </div>
    </div>
  );
}

function KVRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-sm text-slate-900 text-right truncate max-w-[60%] ${mono ? "font-mono-data" : ""}`}>
        {value || <span className="text-slate-400">—</span>}
      </span>
    </div>
  );
}

function Section({ value, title, summary, badge, badgeTone = "slate", fullWidth, children }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    primary: "bg-primary/10 text-primary border-primary/20",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
    slate:   "bg-slate-50 text-slate-600 border-slate-200",
  }[badgeTone] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <AccordionItem
      value={value}
      className={`rounded-lg border border-slate-200 bg-white px-4 shadow-od-subtle data-[state=open]:border-primary/30 ${fullWidth ? "md:col-span-2" : ""}`}
    >
      <AccordionTrigger className="hover:no-underline py-3" data-testid={`section-trigger-${value}`}>
        <div className="flex flex-1 items-center justify-between gap-3 min-w-0">
          <div className="flex flex-col gap-0.5 min-w-0 text-left">
            <span className="font-display text-sm font-bold text-slate-900">{title}</span>
            {summary && (
              <span className="text-[11px] font-medium text-slate-500 truncate">{summary}</span>
            )}
          </div>
          {badge && (
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${toneCls}`}>
              {badge}
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-4 pt-1">{children}</AccordionContent>
    </AccordionItem>
  );
}

function KVGrid({ children }) {
  return <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{children}</dl>;
}

function KV({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
}

function DelayBanner({ tasks }) {
  const delays = (tasks || []).filter((t) => t.delay_marked && t.status !== "completed");
  if (delays.length === 0) return null;
  const label = (type) => (TASK_LABELS[type] || type);
  return (
    <Card className="border-amber-200 bg-amber-50/60 p-4" data-testid="sales-delay-banner">
      <div className="flex items-start gap-3">
        <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-amber-900">
            {delays.length} task{delays.length > 1 ? "s" : ""} marked as delayed
          </div>
          <ul className="mt-2 space-y-1.5 text-sm text-amber-900">
            {delays.map((t) => (
              <li key={t.id} className="rounded border border-amber-200 bg-white/60 px-3 py-2"
                  data-testid={`sales-delay-task-${t.type}`}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-bold uppercase tracking-wider text-amber-800">{label(t.type)}</span>
                  {t.delay_marked_at && (
                    <span className="text-[10px] text-amber-600">{fmtDateTime(t.delay_marked_at)}</span>
                  )}
                </div>
                <div className="mt-0.5 text-sm text-amber-900">{t.delay_remarks || "—"}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Card>
  );
}

// ─── Accordion summary helpers ───────────────────────────────────

function describeFinance(sale, financeCos) {
  if (!sale.finance?.buy_mode) return "Not configured";
  if (sale.finance.buy_mode === "cash") return "Cash purchase";
  const co = financeCos.find((m) => m.id === sale.finance?.finance_company_id);
  return `Finance${co ? ` · ${co.name}` : ""}${sale.finance?.loan_amount ? ` · ${fmt(sale.finance.loan_amount)}` : ""}`;
}

function describeVAS(sale) {
  const bits = [];
  if (sale.vas?.pp_opted !== false) bits.push("PP");
  if (sale.vas?.amc_opted !== false) bits.push("AMC");
  if (sale.vas?.rsa_opted !== false) bits.push("RSA");
  return bits.length ? `Opted: ${bits.join(" · ")}` : "Customer not opted";
}

// ─── Dialogs / Buttons (kept verbatim from previous implementation) ───

function CompleteTaskBtn({ taskId, type, onDone }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    setSaving(true);
    try {
      await Tasks.complete(taskId, { notes });
      toast.success(`${type} task completed`);
      setOpen(false); setNotes("");
      await onDone();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1 h-7 px-2 text-[11px]"
                data-testid={`complete-${type}-btn`}>
          <CheckCircle2 className="h-3 w-3" /> Mark Complete
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Complete {type} Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid={`complete-${type}-notes`} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handle} disabled={saving} data-testid={`confirm-complete-${type}`}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EarlyDeliveryBadge({ status, conditional, reason, rejectionReason, decidedByName }) {
  const meta = (() => {
    if (conditional) {
      return {
        label: "Conditional Delivery",
        cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
        dot: "bg-emerald-500",
        sub: "Delivered with pending process",
      };
    }
    if (status === "pending") {
      return {
        label: "Early Delivery Requested",
        cls: "border-amber-300 bg-amber-50 text-amber-800",
        dot: "bg-amber-500",
        sub: reason ? `Reason: ${reason}` : "Awaiting Accounts approval",
      };
    }
    if (status === "approved") {
      return {
        label: "Early Delivery Approved",
        cls: "border-emerald-300 bg-emerald-50 text-emerald-700",
        dot: "bg-emerald-500",
        sub: decidedByName ? `Approved by ${decidedByName}` : "Ready to mark delivered",
      };
    }
    if (status === "rejected") {
      return {
        label: "Early Delivery Rejected",
        cls: "border-rose-300 bg-rose-50 text-rose-700",
        dot: "bg-rose-500",
        sub: rejectionReason ? `Reason: ${rejectionReason}` : "Submit a new request if needed",
      };
    }
    return null;
  })();
  if (!meta) return null;
  return (
    <div
      className={`inline-flex max-w-full items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold ${meta.cls}`}
      data-testid={`early-delivery-badge-${status}${conditional ? "-conditional" : ""}`}
    >
      <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${meta.dot}`} />
      <span className="font-bold uppercase tracking-wider">{meta.label}</span>
      {meta.sub && <span className="hidden font-normal opacity-90 sm:inline">· {meta.sub}</span>}
    </div>
  );
}



function DeleteDraftButton({ onConfirm }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline"
                className="gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="delete-draft-btn">
          <Trash2 className="h-4 w-4" /> Delete Draft
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Delete this draft?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          This will permanently remove the draft sale and any uploaded documents. This action cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-cancel-btn">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700"
                  onClick={() => { setOpen(false); onConfirm(); }}
                  data-testid="delete-confirm-btn">
            Yes, delete draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteSaleButton({ sale, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const expected = sale.sr_number || sale.id;
  const canDelete = confirmText.trim() === expected;
  const statusLabel = (sale.status || "").replace(/_/g, " ");
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(""); }}>
      <DialogTrigger asChild>
        <Button variant="outline"
                className="gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="delete-sale-btn">
          <Trash2 className="h-4 w-4" /> Delete Sale
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Trash2 className="h-5 w-5" /> Delete Sale {expected}?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            This sale is currently in <strong className="capitalize">{statusLabel}</strong>.
            Deleting will permanently remove the sale, all its tasks, payment receipts, audit chain references
            and soft-delete uploaded documents. <strong>This action cannot be undone.</strong>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Type the SR number <span className="font-mono-data font-semibold text-rose-700">{expected}</span> to confirm
            </Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                   placeholder={expected} autoFocus data-testid="delete-sale-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="delete-sale-cancel-btn">Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
                  disabled={!canDelete}
                  onClick={() => { setOpen(false); onConfirm(); }}
                  data-testid="delete-sale-confirm-btn">
            Delete Sale Permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
